'use client';

import { FormEvent, useState } from 'react';
import { useRouter } from 'next/navigation';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  ApiError,
  forgotPassword,
  login,
  setupEmail,
  verifyTwoFactor,
  type TokenDays,
} from '@torlin/api-client';
import { localePath, loginCopy, type Locale } from '@/i18n/public';
import { saveWebAuthToken } from '@/lib/web-auth-token';

const apiUrl = process.env.NEXT_PUBLIC_API_URL ?? '';

type Step =
  | { kind: 'credentials' }
  | { kind: 'emailSetup' }
  | { kind: 'verification'; maskedEmail: string }
  | { kind: 'forgot' };

function errorMessage(error: unknown, fallback: string, field?: string): string {
  if (error instanceof ApiError) {
    const fieldError = field ? error.errors[field]?.[0] : undefined;
    return fieldError ?? error.message;
  }

  return fallback;
}

export function LoginForm({ locale }: { locale: Locale }) {
  const router = useRouter();
  const copy = loginCopy[locale];
  const [step, setStep] = useState<Step>({ kind: 'credentials' });
  const [loginIdentifier, setLoginIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [tokenDays, setTokenDays] = useState<TokenDays>(30);
  const [email, setEmail] = useState('');
  const [emailConfirmation, setEmailConfirmation] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [forgotLogin, setForgotLogin] = useState('');
  const [forgotStatus, setForgotStatus] = useState<'email_sent' | 'admin_contact' | null>(null);

  async function submitCredentials(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const response = await login(apiUrl, { login: loginIdentifier, password }, locale);
      setPassword('');
      setStep(
        response.status === 'email_setup_required'
          ? { kind: 'emailSetup' }
          : {
              kind: 'verification',
              maskedEmail: response.masked_email,
            },
      );
    } catch (caught) {
      setError(errorMessage(caught, copy.connectionError, 'login'));
    } finally {
      setLoading(false);
    }
  }

  async function submitEmailSetup(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const response = await setupEmail(
        apiUrl,
        email,
        emailConfirmation,
        locale,
      );
      setStep({
        kind: 'verification',
        maskedEmail: response.masked_email,
      });
    } catch (caught) {
      setError(errorMessage(caught, copy.connectionError, 'email'));
    } finally {
      setLoading(false);
    }
  }

  async function submitCode(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const response = await verifyTwoFactor(apiUrl, code, locale, tokenDays);
      saveWebAuthToken(response.token, tokenDays, response.details);
      router.replace(localePath(locale, '/app'));
      router.refresh();
    } catch (caught) {
      setError(errorMessage(caught, copy.connectionError, 'code'));
    } finally {
      setLoading(false);
    }
  }

  async function submitForgotPassword(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setForgotStatus(null);
    setLoading(true);
    try {
      const response = await forgotPassword(apiUrl, forgotLogin, locale);
      setForgotStatus(response.status);
    } catch (caught) {
      setError(errorMessage(caught, copy.connectionError, 'login'));
    } finally {
      setLoading(false);
    }
  }

  if (step.kind === 'forgot') {
    return <Box component="form" onSubmit={submitForgotPassword}><Stack spacing={3}>
      <Box><Typography variant="h4" component="h1" gutterBottom>{copy.forgotTitle}</Typography><Typography color="text.secondary">{copy.forgotIntro}</Typography></Box>
      {error && <Alert severity="error">{error}</Alert>}
      {forgotStatus === 'email_sent' && <Alert severity="success">{copy.forgotSent}</Alert>}
      {forgotStatus === 'admin_contact' && <Alert severity="info">{copy.noEmailAccount}</Alert>}
      <TextField autoFocus fullWidth label={copy.login} value={forgotLogin} onChange={(event) => setForgotLogin(event.target.value)} autoComplete="username" required />
      <Button type="submit" size="large" variant="contained" disabled={loading || !forgotLogin.trim()}>{loading ? <CircularProgress size={24} color="inherit" /> : copy.forgotAction}</Button>
      <Button type="button" color="inherit" onClick={() => { setForgotStatus(null); setError(null); setStep({ kind: 'credentials' }); }}>{copy.backToLogin}</Button>
    </Stack></Box>;
  }

  if (step.kind === 'emailSetup') {
    return (
      <Box component="form" onSubmit={submitEmailSetup}>
        <Stack spacing={3}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              {copy.emailSetupTitle}
            </Typography>
            <Typography color="text.secondary">
              {copy.emailSetupIntro}
            </Typography>
          </Box>

          {error && <Alert severity="error">{error}</Alert>}

          <TextField
            autoFocus
            fullWidth
            type="email"
            label={copy.email}
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            autoComplete="email"
            required
          />
          <TextField
            fullWidth
            type="email"
            label={copy.emailConfirmation}
            value={emailConfirmation}
            onChange={(event) => setEmailConfirmation(event.target.value)}
            autoComplete="email"
            required
          />

          <Button
            type="submit"
            size="large"
            variant="contained"
            disabled={loading || email === '' || email !== emailConfirmation}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : copy.emailSetupAction}
          </Button>

          <Button
            type="button"
            color="inherit"
            onClick={() => {
              setEmail('');
              setEmailConfirmation('');
              setError(null);
              setStep({ kind: 'credentials' });
            }}
          >
            {copy.backToLogin}
          </Button>
        </Stack>
      </Box>
    );
  }

  if (step.kind === 'verification') {
    return (
      <Box component="form" onSubmit={submitCode}>
        <Stack spacing={3}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              {copy.verificationTitle}
            </Typography>
            <Typography color="text.secondary">
              {copy.verificationIntro} {step.maskedEmail}.{' '}
              {copy.verificationValidity}
            </Typography>
          </Box>

          {error && <Alert severity="error">{error}</Alert>}

          <TextField
            autoFocus
            fullWidth
            label={copy.code}
            value={code}
            onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
            slotProps={{
              htmlInput: {
                inputMode: 'numeric',
                autoComplete: 'one-time-code',
              },
            }}
            required
          />

          <Button
            type="submit"
            size="large"
            variant="contained"
            disabled={loading || code.length !== 6}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : copy.verify}
          </Button>

          <Button
            type="button"
            color="inherit"
            onClick={() => {
              setCode('');
              setError(null);
              setStep({ kind: 'credentials' });
            }}
          >
            {copy.backToLogin}
          </Button>
        </Stack>
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={submitCredentials}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            {copy.title}
          </Typography>
          <Typography color="text.secondary">
            {copy.intro}
          </Typography>
        </Box>

        {error && <Alert severity="error">{error}</Alert>}

        <TextField
          autoFocus
          fullWidth
          type="text"
          label={copy.login}
          value={loginIdentifier}
          onChange={(event) => setLoginIdentifier(event.target.value)}
          autoComplete="username"
          required
        />
        <TextField
          select
          fullWidth
          label={copy.tokenDuration}
          value={tokenDays}
          onChange={(event) => setTokenDays(Number(event.target.value) as TokenDays)}
        >
          <MenuItem value={1}>{copy.tokenDays.one}</MenuItem>
          <MenuItem value={7}>{copy.tokenDays.seven}</MenuItem>
          <MenuItem value={30}>{copy.tokenDays.thirty}</MenuItem>
          <MenuItem value={90}>{copy.tokenDays.ninety}</MenuItem>
          <MenuItem value={365}>{copy.tokenDays.year}</MenuItem>
        </TextField>
        <TextField
          fullWidth
          type="password"
          label={copy.password}
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          autoComplete="current-password"
          required
        />

        <Button type="submit" size="large" variant="contained" disabled={loading}>
          {loading ? <CircularProgress size={24} color="inherit" /> : copy.continue}
        </Button>
        <Button type="button" color="inherit" onClick={() => { setForgotLogin(loginIdentifier); setForgotStatus(null); setError(null); setStep({ kind: 'forgot' }); }}>{copy.forgotPassword}</Button>
      </Stack>
    </Box>
  );
}
